import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BarChart3, Heart, MessageCircle, Bookmark, Eye, Share2, Instagram, Youtube, Facebook, Twitter, FileText, LinkIcon, Plug } from "lucide-react";
import { useAuth } from "../lib/auth";

const PLATFORMS = { Instagram: Instagram, YouTube: Youtube, Facebook: Facebook, Twitter: Twitter, Blog: FileText };

const KPI_DEFS = [
  { key: "likes", label: "Likes", icon: Heart, color: "#E4405F" },
  { key: "comments", label: "Comments", icon: MessageCircle, color: "#1DA1F2" },
  { key: "shares", label: "Shares", icon: Share2, color: "#1B7A3E" },
  { key: "saves", label: "Saves", icon: Bookmark, color: "#F59E0B" },
  { key: "reach", label: "Reach", icon: Eye, color: "#8B5CF6" },
  { key: "impressions", label: "Impressions", icon: BarChart3, color: "#0EA5E9" },
];

function KpiCards({ totals }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {KPI_DEFS.map(k => {
        const Icon = k.icon;
        return (
          <div key={k.key} className="aii-card p-4" data-testid={`kpi-${k.key}`}>
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[color:var(--aii-muted)]"><Icon size={13} style={{ color: k.color }}/> {k.label}</div>
            <div className="text-2xl font-display font-semibold mt-1">{(totals?.[k.key] || 0).toLocaleString()}</div>
          </div>
        );
      })}
    </div>
  );
}

function ByPlatformGrid({ byPlatform }) {
  return (
    <div className="aii-card p-5">
      <h3 className="font-semibold mb-3">By platform</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {Object.entries(byPlatform).map(([p, v]) => {
          const Icon = PLATFORMS[p] || FileText;
          return (
            <div key={p} className="border border-[color:var(--aii-border)] rounded-lg p-3">
              <div className="flex items-center gap-1.5 text-sm font-semibold"><Icon size={14} className="text-brand"/> {p}</div>
              <div className="text-xs text-[color:var(--aii-muted)] mt-1">{v.posts} post{v.posts !== 1 ? "s" : ""}</div>
              <div className="mt-2 space-y-0.5 text-xs">
                <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Likes</span><strong>{v.likes.toLocaleString()}</strong></div>
                <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Reach</span><strong>{v.reach.toLocaleString()}</strong></div>
                <div className="flex justify-between"><span className="text-[color:var(--aii-muted)]">Impressions</span><strong>{v.impressions.toLocaleString()}</strong></div>
              </div>
            </div>
          );
        })}
        {Object.keys(byPlatform).length === 0 && <div className="text-sm text-[color:var(--aii-muted)] italic col-span-full py-6 text-center">Publish content to see platform stats.</div>}
      </div>
    </div>
  );
}

function PublishedPostsTable({ published, publishedCount, canEdit, onEdit }) {
  return (
    <div className="aii-card overflow-x-auto">
      <div className="p-5 border-b border-[color:var(--aii-border)]">
        <h3 className="font-semibold">Published posts <span className="text-sm text-[color:var(--aii-muted)] font-normal">· {publishedCount} total</span></h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-1">Click &ldquo;Log metrics&rdquo; to enter engagement numbers manually. Once Instagram is connected, these will sync automatically.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="aii-table">
          <thead><tr>
            <th>Platform</th><th>Post</th><th>Date</th><th>Likes</th><th>Comments</th><th>Shares</th><th>Saves</th><th>Reach</th><th>Impressions</th><th></th>
          </tr></thead>
          <tbody>
            {published.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-[color:var(--aii-muted)]">No published posts yet.</td></tr>}
            {published.map(p => {
              const m = p.metrics || {};
              const Icon = PLATFORMS[p.platform] || FileText;
              return (
                <tr key={p.id} data-testid={`ana-row-${p.id}`}>
                  <td><span className="inline-flex items-center gap-1.5"><Icon size={13} className="text-brand"/> {p.platform}</span></td>
                  <td className="font-medium">{p.title}</td>
                  <td>{p.scheduled_date || "—"}</td>
                  <td>{(m.likes || 0).toLocaleString()}</td>
                  <td>{(m.comments || 0).toLocaleString()}</td>
                  <td>{(m.shares || 0).toLocaleString()}</td>
                  <td>{(m.saves || 0).toLocaleString()}</td>
                  <td>{(m.reach || 0).toLocaleString()}</td>
                  <td>{(m.impressions || 0).toLocaleString()}</td>
                  <td className="text-right">
                    {canEdit && (
                      <button data-testid={`log-metrics-${p.id}`} onClick={() => onEdit(p)}
                        className="text-xs bg-brand text-white px-2 py-1 rounded hover:bg-brand-dark">Log metrics</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricsModal({ post, metrics, setMetrics, onSave, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-lg">{post.title}</h3>
          <p className="text-xs text-[color:var(--aii-muted)]">{post.platform} · {post.scheduled_date || "no date"}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {KPI_DEFS.map(k => (
            <div key={k.key}>
              <label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--aii-text-2)]">{k.label}</label>
              <input type="number" data-testid={`m-${k.key}`} value={metrics[k.key]} onChange={e=>setMetrics(v=>({...v, [k.key]: parseInt(e.target.value)||0}))} className="input-aii"/>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm">Cancel</button>
          <button data-testid="save-metrics" onClick={onSave} className="bg-brand text-white font-semibold px-5 py-2 rounded-md hover:bg-brand-dark">Save metrics</button>
        </div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [published, setPublished] = useState([]);
  const [editing, setEditing] = useState(null);
  const [metrics, setMetrics] = useState({ likes: 0, comments: 0, shares: 0, saves: 0, reach: 0, impressions: 0 });

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/content/analytics/summary"),
      api.get("/content"),
    ]);
    setSummary(s.data);
    setPublished(c.data.filter(x => x.status === "published"));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    const m = p.metrics || {};
    setMetrics({
      likes: m.likes || 0, comments: m.comments || 0, shares: m.shares || 0,
      saves: m.saves || 0, reach: m.reach || 0, impressions: m.impressions || 0,
    });
    setEditing(p);
  };

  const saveMetrics = async () => {
    await api.patch(`/content/${editing.id}/metrics`, metrics);
    toast.success("Metrics saved");
    setEditing(null);
    await load();
  };

  const canEdit = ["SMCM", "FOUNDER", "BDOM"].includes(user.effective_role);

  if (!summary) return <div className="text-sm text-[color:var(--aii-muted)]">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="analytics-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="text-brand"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Social Media</div>
            <h1 className="text-2xl font-semibold">Content Analytics</h1>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className={`pill ${summary.instagram_connected ? "pill-green" : "pill-amber"} flex items-center gap-1`}>
            <Plug size={11}/> {summary.instagram_connected ? "Instagram connected" : "Instagram not connected"}
          </span>
          <button data-testid="connect-ig" onClick={() => toast.info("Instagram Graph API integration coming — bring your Meta Business account details when ready.")}
            className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5">
            <LinkIcon size={12}/> Connect Instagram
          </button>
        </div>
      </div>

      <KpiCards totals={summary.totals}/>
      <ByPlatformGrid byPlatform={summary.by_platform}/>
      <PublishedPostsTable published={published} publishedCount={summary.published_count} canEdit={canEdit} onEdit={openEdit}/>

      {editing && (
        <MetricsModal post={editing} metrics={metrics} setMetrics={setMetrics}
          onSave={saveMetrics} onCancel={() => setEditing(null)}/>
      )}
    </div>
  );
}
